import React, { useState, useEffect, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { TextField, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useAuth, useAlert, useCancelShooting } from 'hooks';
import { hideModal } from 'state/actions';
import { LOADING } from 'state/constants';
import { ALERT, MODAL } from 'config/constants';
import { HvModal, HvButton, HvPleaseWait } from 'components';
import classNames from 'classnames';

const useStyles = makeStyles({
  description: {
    fontSize: 14,
    marginBottom: 16,
  },
  inputBox: {
    width: '100%',
    height: 100,

    '& .MuiInputBase-root': {
      height: '100%',
      alignItems: 'flex-start',
    },
  },
  nevermind: {
    textTransform: 'none',
    textDecoration: 'underline',
    marginRight: 'auto',
  },
  invalidInput: {
    '& .MuiInputBase-root': {
      borderColor: 'red',
      borderWidth: '1px',
      borderStyle: 'solid',
    },
  },
});

interface Props {
  shootingId: number;
}

function ShootingCancelModal({ shootingId }: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();

  const { token } = useAuth();
  const { displayAlert } = useAlert();
  const { cancelling, cancelled, cancelShooting } = useCancelShooting();

  const [reason, setReason] = useState('');
  const [canCancel, setCanCancel] = useState(false);

  const onReasonChange = (e: ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  };

  const onClose = () => {
    dispatch(hideModal(MODAL.SHOOTING_CANCEL));
  };

  const onConfirmCancel = async () => {
    if (reason.length === 0) {
      setCanCancel(false);
    } else {
      setCanCancel(true);
      cancelShooting(token, shootingId, reason);
    }
  };

  const onFocusTextField = () => {
    setCanCancel(true);
  };

  useEffect(() => {
    if (cancelling !== LOADING.FINISHED) {
      return;
    }

    if (cancelled) {
      displayAlert(ALERT.SUCCESS, intl.formatMessage({ id: 'shooting.detail.cancelSuccess' }));
    } else {
      displayAlert(ALERT.ERROR, intl.formatMessage({ id: 'shooting.detail.cancelError' }));
    }

    onClose();
  }, [cancelling]);

  const buttons = (
    <>
      <Button className={styles.nevermind} onClick={onClose}>
        {intl.formatMessage({ id: 'shooting.detail.nevermind' })}
      </Button>
      <HvButton onClick={onConfirmCancel}>
        {intl.formatMessage({ id: 'shooting.detail.cancelShoot' })}
      </HvButton>
    </>
  );

  return (
    <HvModal
      title={intl.formatMessage({ id: 'shooting.detail.cancelShoot' })}
      buttons={buttons}
      onClose={onClose}
    >
      <Typography className={styles.description}>
        {intl.formatMessage({ id: 'shooting.detail.cancelReason' })}
      </Typography>

      <TextField
        className={classNames(styles.inputBox, !canCancel && styles.invalidInput)}
        variant='outlined'
        multiline
        value={reason}
        onChange={onReasonChange}
        onFocus={onFocusTextField}
      />

      {cancelling === LOADING.STARTED ? <HvPleaseWait /> : null}
    </HvModal>
  );
}
export default ShootingCancelModal;
